import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import type { ChartConfig } from '../../../types';
import { CHART_STYLE, formatDataValue, analyzeDecimals } from './chartStyles';

interface LineChartViewProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
}

/** Simple X–Y line chart. Resolves data keys and sets explicit Y domain so the line and axis render. */
export function LineChartView({ data, config }: LineChartViewProps) {
  const xKey = config.xAxis ?? '';
  const yKeys = Array.isArray(config.yAxis) ? config.yAxis : [config.yAxis ?? ''];
  const isMulti = yKeys.length > 1;

  // Resolve y-keys that actually exist in the data (handles e.g. "Total TEUs" with space)
  const { domain, validYKeys } = useMemo(() => {
    if (!data || data.length === 0) {
      return { domain: [0, 100] as [number, number], validYKeys: yKeys };
    }
    const firstRow = data[0];
    if (typeof firstRow !== 'object' || firstRow === null) {
      return { domain: [0, 100] as [number, number], validYKeys: yKeys };
    }
    const dataKeys = Object.keys(firstRow);
    const resolved = yKeys.map((key) => {
      const target = (key ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
      return dataKeys.find(
        (k) => k.toLowerCase().replace(/[^a-z0-9]/g, '') === target,
      ) ?? key;
    }).filter(Boolean) as string[];

    let min = Infinity;
    let max = -Infinity;
    data.forEach((row) => {
      resolved.forEach((key) => {
        const v = row[key];
        const num = typeof v === 'string' ? Number(v.replace(/,/g, '')) : Number(v);
        if (!isNaN(num)) {
          if (num < min) min = num;
          if (num > max) max = num;
        }
      });
    });

    const yMin = min === Infinity ? 0 : min;
    const yMax = max === -Infinity ? 100 : Math.ceil(max * 1.1);
    return {
      domain: [yMin, yMax] as [number, number],
      validYKeys: resolved.length > 0 ? resolved : yKeys,
    };
  }, [data, yKeys]);

  const { hasDecimals, allSmall } = analyzeDecimals(data, validYKeys[0] ?? yKeys[0]);

  return (
    <ResponsiveContainer width="100%" height="100%" minHeight={300}>
      <LineChart data={data} margin={{ top: 10, right: 10, bottom: 5, left: 40 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 10, fill: '#6B7280' }}
          height={30}
          angle={0}
          textAnchor="middle"
          interval={0}
        />
        <YAxis
          domain={domain}
          width={40}
          tick={{ fontSize: 10, fill: '#6B7280' }}
          tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v))}
        />
        <Tooltip
          contentStyle={{ fontSize: 11, padding: '6px 10px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          formatter={(value: unknown, name?: string) => [formatDataValue(Number(value ?? 0), hasDecimals, allSmall), name ?? '']}
          labelFormatter={(label) => (label != null ? String(label) : '')}
        />
        {isMulti && <Legend wrapperStyle={{ fontSize: 10 }} />}
        {validYKeys.map((key, i) => (
          <Line
            key={key}
            type="monotone"
            dataKey={key}
            stroke={CHART_STYLE.colors[i % CHART_STYLE.colors.length]}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
